/**
 * Inline scene previews (Block 6.2). For each element in the open board that
 * requests a preview (customData.ryfeSceneLink.mode === "preview"), render a
 * STATIC thumbnail of the target scene positioned over that element.
 *
 * Rights: the target is loaded through the normal authenticated content
 * endpoint, so the central guard (#7) applies automatically — there is NO
 * separate preview endpoint and NO baked-in snapshot (that would be the bypass
 * that turns a preview into a rights leak). On 403 we show a visible
 * "kein Zugriff" placeholder rather than a silent blank, so a locked scene is
 * distinguishable from an empty one. A static image + click-to-open is
 * preferred over a live frame (Block 6.3).
 */

import {
  exportToSvg,
  sceneCoordsToViewportCoords,
} from "@excalidraw/excalidraw";
import { useEffect, useRef, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { ApiError, ryfeApi } from "../api";
import { parseScene } from "../board-data";
import {
  displayModeScale,
  getDisplayMode,
  resolveMainFrame,
} from "../frame-model";
import { renderMarkdown } from "../markdown";
import { routeForKind } from "../open-file";
import { navigate } from "../router";

import "./ryfe.scss";

import type { PreviewLink } from "../scene-links";

// CC_07 Block 3 (D-39) — labels for the non-board click-through tile.
const KIND_LABEL: Record<string, string> = {
  board: "Board",
  md: "Notiz",
  list: "Liste",
};

type PreviewState =
  | { status: "loading" }
  | { status: "ok"; svg: string }
  // In-Scene-Vorschau (D-43): rendered content of a note / list target.
  | { status: "md"; html: string; name?: string }
  | { status: "list"; cols: string[]; rows: string[][]; name?: string }
  // Fallback tile for a kind without an in-scene renderer.
  | { status: "tile"; kind: string; name?: string }
  | { status: "denied" }
  | { status: "empty" }
  | { status: "error" };

export const ScenePreviewOverlay = ({
  wsId,
  api,
  links,
  version,
}: {
  wsId: string;
  api: ExcalidrawImperativeAPI;
  links: PreviewLink[];
  version: number;
}) => {
  const [cache, setCache] = useState<Record<string, PreviewState>>({});
  // Target kind, resolved from meta as soon as it loads — drives route-by-kind
  // on click, so even a still-loading target opens in the correct editor.
  const [kinds, setKinds] = useState<Record<string, string>>({});
  const inflight = useRef<Set<string>>(new Set());
  const started = useRef<Set<string>>(new Set());
  // Cancellation is tied to the component lifetime, NOT to each effect run.
  // (The previous per-effect token, combined with `cache` in the deps, cancelled
  // itself on the very next render — the setState that marked a target "loading"
  // re-ran the effect, whose cleanup flipped the token, so the completed load's
  // "ok" result was dropped and the preview stayed stuck on "loading" forever.)
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Load each distinct target scene exactly once. Depends on `links`/`wsId`
  // only; `started` de-dupes across re-renders without re-triggering the effect.
  useEffect(() => {
    const loadOne = async (id: string, name?: string) => {
      try {
        // Resolve the kind first (D-39): only a board is an Excalidraw scene we
        // can render to SVG. md/list targets get a click-through tile instead —
        // their blob is not a scene, so parseScene/exportToSvg must not run.
        const meta = await ryfeApi.getFile(wsId, id).catch(() => null);
        const kind = meta?.kind || "board";
        if (mounted.current) {
          setKinds((k) => ({ ...k, [id]: kind }));
        }
        // In-Scene-Vorschau (D-43): render the note / list content itself.
        if (kind === "md") {
          const buf = await ryfeApi.getFileContent(wsId, id);
          const text = new TextDecoder()
            .decode(new Uint8Array(buf))
            .slice(0, 1200);
          if (mounted.current) {
            setCache((c) => ({
              ...c,
              [id]: { status: "md", html: renderMarkdown(text), name },
            }));
          }
          return;
        }
        if (kind === "list") {
          const snap = await ryfeApi.getList(wsId, id);
          const cols = snap.columns.slice(0, 4);
          const rows = snap.rows.slice(0, 5).map((r) =>
            cols.map((col) => {
              const v = r.data[col.id];
              return v === undefined || v === null ? "" : String(v);
            }),
          );
          if (mounted.current) {
            setCache((c) => ({
              ...c,
              [id]: {
                status: "list",
                cols: cols.map((col) => col.name),
                rows,
                name,
              },
            }));
          }
          return;
        }
        if (kind !== "board") {
          if (mounted.current) {
            setCache((c) => ({ ...c, [id]: { status: "tile", kind, name } }));
          }
          return;
        }
        // The board's main-frame POINTER lives on the file meta (Block B.2),
        // not in the scene blob. The preview shows that frame's region; an
        // empty/stale/embed pointer → whole scene.
        const buf = await ryfeApi.getFileContent(wsId, id);
        const text = new TextDecoder().decode(new Uint8Array(buf));
        const scene = parseScene(text);
        const elements = (scene.elements || []) as never[];
        if (!elements.length) {
          if (mounted.current) {
            setCache((c) => ({ ...c, [id]: { status: "empty" } }));
          }
          return;
        }
        const mainFrame = resolveMainFrame(elements, meta?.main_frame_id);
        const svg = await exportToSvg({
          elements,
          appState: { ...scene.appState, exportBackground: true },
          files: (scene.files || {}) as never,
          exportingFrame: mainFrame as never,
          skipInliningFonts: true,
        });
        svg.setAttribute("width", "100%");
        svg.setAttribute("height", "100%");
        const str = new XMLSerializer().serializeToString(svg);
        if (mounted.current) {
          setCache((c) => ({ ...c, [id]: { status: "ok", svg: str } }));
        }
      } catch (e) {
        const denied = e instanceof ApiError && e.status === 403;
        if (mounted.current) {
          setCache((c) => ({
            ...c,
            [id]: { status: denied ? "denied" : "error" },
          }));
        }
      } finally {
        inflight.current.delete(id);
      }
    };
    for (const { data } of links) {
      const id = data.targetFileId;
      if (started.current.has(id) || inflight.current.has(id)) {
        continue;
      }
      started.current.add(id);
      inflight.current.add(id);
      setCache((c) => ({ ...c, [id]: { status: "loading" } }));
      loadOne(id, data.targetName);
    }
  }, [links, wsId]);

  const state = api.getAppState();
  const zoom = state.zoom?.value ?? 1;

  return (
    <div className="ryfe-preview-layer" aria-hidden={false}>
      {links.map(({ element, data }) => {
        const tl = sceneCoordsToViewportCoords(
          { sceneX: element.x, sceneY: element.y },
          state,
        );
        const left = tl.x - (state.offsetLeft ?? 0);
        const top = tl.y - (state.offsetTop ?? 0);
        const w = element.width * zoom;
        const hgt = element.height * zoom;
        const p = cache[data.targetFileId];
        // Reserve the label strip drawn on the element itself.
        const pad = Math.min(28, hgt * 0.18);
        // CC_10 Block 1 (D-54), Fix CC_11-Revision — the inner content gets a
        // FIXED size in SCENE units (element.width/height) and is scaled by the
        // ABSOLUTE mode scale. The previous relative counter-scale was visually
        // a no-op for SVG previews (the SVG re-fit itself into the scaled box),
        // so the three modes looked identical. Now:
        //   scaleWithCanvas → scale = zoom  (content fills the box, as before)
        //   keepReadable    → scale = max(zoom, 1)  (never shrinks below 1:1)
        //   noScale         → scale = 1  (fixed 1:1, independent of zoom)
        // Overflow is clipped by `.ryfe-preview` (conservative, D-54).
        const mode = getDisplayMode(element);
        const modeScale = displayModeScale(mode, zoom);
        const scalerStyle = {
          width: element.width,
          height: Math.max(0, element.height - pad / (zoom || 1)),
          transform: `scale(${modeScale})`,
          transformOrigin: "top left" as const,
          flex: "0 0 auto",
        };
        return (
          <div
            key={element.id}
            className="ryfe-preview"
            style={{
              left,
              top: top + pad,
              width: w,
              height: Math.max(0, hgt - pad),
            }}
            onClick={() => {
              // Route by the target's kind (D-39), never hard to the board —
              // opening a note/list in the board editor was the latent
              // mis-open path this closes. Unknown kind → do nothing.
              const route = routeForKind(
                kinds[data.targetFileId] || "board",
                data.targetFileId,
              );
              if (route) {
                navigate(route);
              }
            }}
            title={`Öffnen: ${data.targetName || "Szene"}`}
          >
            <div className="ryfe-preview__scaler" style={scalerStyle}>
              {!p || p.status === "loading" ? (
                <span className="ryfe-preview__note">Vorschau lädt…</span>
              ) : p.status === "ok" ? (
                // eslint-disable-next-line react/no-danger
                <div
                  className="ryfe-preview__svg"
                  dangerouslySetInnerHTML={{ __html: p.svg }}
                />
              ) : p.status === "md" ? (
                // eslint-disable-next-line react/no-danger
                <div
                  className="ryfe-preview__md"
                  dangerouslySetInnerHTML={{ __html: p.html }}
                />
              ) : p.status === "list" ? (
                <div className="ryfe-preview__list">
                  <table>
                    <thead>
                      <tr>
                        {p.cols.map((c, i) => (
                          <th key={i}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {p.rows.map((r, i) => (
                        <tr key={i}>
                          {r.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : p.status === "tile" ? (
                <span className="ryfe-preview__note ryfe-preview__tile">
                  {KIND_LABEL[p.kind] || p.kind} · {p.name || "öffnen"} ↗
                </span>
              ) : p.status === "denied" ? (
                <span className="ryfe-preview__note ryfe-preview__note--denied">
                  🔒 kein Zugriff
                </span>
              ) : p.status === "empty" ? (
                <span className="ryfe-preview__note">leere Szene</span>
              ) : (
                <span className="ryfe-preview__note">
                  Vorschau nicht verfügbar
                </span>
              )}
            </div>
          </div>
        );
      })}
      {/* re-render trigger so positions follow scroll/zoom */}
      <span hidden data-v={version} />
    </div>
  );
};
